import { useState, useRef, useEffect } from "react";
import AppNavbar from "../components/common/AppNavbar";
import { Helmet } from "react-helmet";

function useInView(ref, threshold = 0.1) {
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setV(true);
      },
      { threshold }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return v;
}

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref);
  return (
    <div
      ref={ref}
      style={{
        opacity: v ? 1 : 0,
        transform: v ? "none" : "translateY(20px)",
        transition: `opacity .55s ease ${delay}s, transform .55s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

const subjects = [
  "General Enquiry",
  "Community Partnership",
  "Event Collaboration",
  "Media & Press",
  "Technical Support",
  "Feedback & Suggestions",
];

const faqs = [
  {
    q: "Who can join Bharatabroad?",
    a:
      "Any Bharat national living abroad — students, professionals, entrepreneurs, or families. All are welcome.",
  },
  {
    q: "How do I list a community event?",
    a:
      "Use the contact form above with subject 'Event Collaboration' and we'll get you onboarded quickly.",
  },
  {
    q: "Can organisations partner with us?",
    a:
      "Absolutely! Diaspora orgs, cultural groups, and NGOs are encouraged to reach out via our partnership enquiry.",
  },
  {
    q: "Is the platform free to use?",
    a:
      "Core community features are free. Some premium features for organisers may apply. Stay tuned.",
  },
];

const socials = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/thebharatabroad/",
    handle: "@thebharatabroad",
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
        <path d="M22 12c0-5.522-4.478-10-10-10S2 6.478 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/thebharatabroad/",
    handle: "@thebharatabroad",
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <defs>
          <linearGradient
            id="ig"
            x1="0"
            y1="24"
            x2="24"
            y2="0"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#f9ce34" />
            <stop offset="30%" stopColor="#ee2a7b" />
            <stop offset="70%" stopColor="#6228d7" />
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig)" />
        <circle
          cx="12"
          cy="12"
          r="4.5"
          stroke="white"
          strokeWidth="1.8"
          fill="none"
        />
        <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
      </svg>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@TheBharatAbroad",
    handle: "@TheBharatAbroad",
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF0000">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    label: "WhatsApp Community",
    href: "https://chat.whatsapp.com/LNfGI3UFWbt69VeUbBoxLj",
    handle: "Join our Community Group",
    svg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
      </svg>
    ),
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    country: "",
    message: "",
  });
  const [focused, setFocused] = useState(null);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit =
    form.name.trim() && form.email.trim() && form.message.trim();

  const inputStyle = (key) => ({
    width: "100%",
    background: focused === key ? "#f8fafc" : "#fff",
    border: `1.5px solid ${focused === key ? "#2c5364" : "#dde2ea"}`,
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 14,
    color: "#111",
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color .2s, background .2s, box-shadow .2s",
    boxSizing: "border-box",
    boxShadow: focused === key ? "0 0 0 3px rgba(44,83,100,0.08)" : "none",
  });

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>
          Contact Us | Bharat Abroad — Connect with the Global Indian Community
        </title>
        <meta
          name="description"
          content="Get in touch with Bharat Abroad. Whether you're a community leader, partner, or an Indian professional navigating life abroad — we'd love to hear from you."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.bharatabroad.com/contact" />
        <meta property="og:title" content="Contact Us | Bharat Abroad" />
        <meta
          property="og:description"
          content="Reach out to Bharat Abroad for community partnerships, event collaborations, media enquiries, or general support."
        />
        <meta
          property="og:url"
          content="https://www.bharatabroad.com/contact"
        />
        <meta property="og:site_name" content="Bharat Abroad" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@BharatAbroad" />
        <meta name="twitter:title" content="Contact Us | Bharat Abroad" />
      </Helmet>

      <AppNavbar />

      <style>{`
        .contact-page * { box-sizing: border-box; }
        .contact-page {
          background: #f4f7fb;
          color: #1a1a1a;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          min-height: 100vh;
        }
        ::placeholder { color: #aab; font-size: 13px; }
        select option { background: #fff; color: #111; }

        /* ── Hero ── */
        .contact-hero {
          background: linear-gradient(135deg, #0f2027, #203a43, #2c5364);
          color: #fff;
          padding: 60px 0;
          border-bottom: 3px solid #c8a84b;
        }
        .contact-hero-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #c8a84b;
          margin-bottom: 14px;
        }
        .contact-hero h1 {
          font-size: clamp(28px, 5vw, 46px);
          font-weight: 700;
          line-height: 1.15;
          margin: 0 0 14px;
          color: #fff;
        }
        .contact-hero p {
          font-size: 16px;
          color: rgba(255,255,255,0.65);
          max-width: 560px;
          line-height: 1.7;
          margin: 0;
        }

        /* ── Main grid ── */
        .contact-main { max-width: 1100px; margin: 0 auto; padding: 56px 24px 80px; }
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 340px;
          gap: 36px;
          align-items: start;
        }

        /* ── Form card ── */
        .form-card {
          background: #fff;
          border: 1px solid #dde2ea;
          border-radius: 12px;
          padding: 40px;
        }
        .form-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: #0f2027;
          margin: 0 0 28px;
        }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .form-field { margin-bottom: 16px; }
        .form-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #555;
          margin-bottom: 7px;
        }
        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #0f2027, #2c5364);
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 15px 28px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          letter-spacing: 0.04em;
          transition: opacity .2s, transform .2s;
          font-family: inherit;
        }
        .submit-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .submit-btn:disabled { background: #e0e4ea; color: #aaa; cursor: not-allowed; transform: none; }
        .form-privacy {
          font-size: 11px;
          color: #aaa;
          text-align: center;
          margin-top: 12px;
        }

        /* ── Success state ── */
        .success-box { text-align: center; padding: 52px 16px; }
        .success-box h2 { font-size: 26px; color: #0f2027; margin: 16px 0 10px; font-weight: 700; }
        .success-box p { font-size: 15px; color: #555; line-height: 1.75; max-width: 340px; margin: 0 auto 24px; }
        .success-icon { font-size: 52px; display: block; }
        .reset-btn {
          background: transparent;
          border: 1.5px solid #2c5364;
          color: #2c5364;
          border-radius: 8px;
          padding: 11px 28px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: background .2s, color .2s;
        }
        .reset-btn:hover { background: #2c5364; color: #fff; }

        /* ── Right sidebar ── */
        .sidebar-card {
          background: #fff;
          border: 1px solid #dde2ea;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 20px;
        }
        .sidebar-card h3 {
          font-size: 14px;
          font-weight: 700;
          color: #0f2027;
          margin: 0 0 16px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding-bottom: 10px;
          border-bottom: 1px solid #e8edf2;
        }

        /* Info items */
        .info-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          padding: 10px 0;
          border-bottom: 1px solid #f0f2f5;
        }
        .info-item:last-child { border-bottom: none; }
        .info-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
        .info-label { font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #888; margin-bottom: 2px; }
        .info-val { font-size: 13.5px; color: #222; line-height: 1.5; }

        /* Social links */
        .social-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border: 1px solid #e8edf2;
          border-radius: 8px;
          text-decoration: none;
          color: #1a1a1a;
          margin-bottom: 8px;
          transition: border-color .18s, background .18s;
          background: #fff;
        }
        .social-link:last-child { margin-bottom: 0; }
        .social-link:hover { border-color: #2c5364; background: #f4f7fb; }
        .social-icon-wrap {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: #f4f7fb;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .social-info { flex: 1; min-width: 0; }
        .social-name { font-size: 13px; font-weight: 600; color: #111; }
        .social-handle { font-size: 11px; color: #888; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .social-arrow { font-size: 12px; color: #bbb; flex-shrink: 0; }

        /* ── FAQ ── */
        .faq-section {
          background: linear-gradient(135deg, #0f2027, #203a43);
          padding: 72px 24px 80px;
        }
        .faq-inner { max-width: 760px; margin: 0 auto; }
        .faq-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #c8a84b;
          text-align: center;
          margin-bottom: 10px;
        }
        .faq-heading {
          font-size: clamp(22px, 4vw, 32px);
          font-weight: 700;
          color: #fff;
          text-align: center;
          margin: 0 0 40px;
        }
        .faq-item {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          margin-bottom: 10px;
          overflow: hidden;
          cursor: pointer;
          transition: background .2s, border-color .2s;
        }
        .faq-item.open { background: rgba(200,168,75,0.07); border-color: rgba(200,168,75,0.3); }
        .faq-question {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 20px;
          gap: 14px;
        }
        .faq-q-text { font-size: 15px; font-weight: 600; color: #fff; line-height: 1.4; }
        .faq-item.open .faq-q-text { color: #c8a84b; }
        .faq-toggle { font-size: 18px; color: #c8a84b; flex-shrink: 0; transition: transform .24s; line-height: 1; }
        .faq-item.open .faq-toggle { transform: rotate(45deg); }
        .faq-answer {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height .32s ease, opacity .24s ease;
        }
        .faq-item.open .faq-answer { max-height: 200px; opacity: 1; }
        .faq-answer-inner {
          padding: 0 20px 18px;
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          line-height: 1.8;
          border-top: 1px solid rgba(255,255,255,0.07);
          padding-top: 14px;
        }

        /* ── Bottom stripe ── */
        .tricolor { display: flex; }
        .tricolor-band { flex: 1; height: 4px; }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr; }
          .form-row { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .contact-hero { padding: 40px 0; }
          .form-card { padding: 24px 20px; }
          .contact-main { padding: 36px 16px 60px; }
        }
      `}</style>

      <div className="contact-page">
        {/* ════ HERO ════ */}
        <div className="contact-hero">
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
            <div className="contact-hero-eyebrow">Contact Us</div>
            <h1>Let's Connect</h1>
            <p>
              Whether you're a community leader, partner, or a Bharat national
              navigating life abroad — we'd love to hear from you.
            </p>
          </div>
        </div>

        {/* ════ MAIN ════ */}
        <div className="contact-main">
          <div className="contact-grid">
            {/* ── Form ── */}
            <Reveal delay={0.05}>
              <div className="form-card">
                <h2>Send Us a Message</h2>

                {sent ? (
                  <div className="success-box">
                    <span className="success-icon">🙏</span>
                    <h2>Dhanyavaad!</h2>
                    <p>
                      Your message has been received. Our team will get back to
                      you within 24–48 hours.
                    </p>
                    <button
                      className="reset-btn"
                      onClick={() => {
                        setSent(false);
                        setForm({
                          name: "",
                          email: "",
                          subject: "",
                          country: "",
                          message: "",
                        });
                      }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="form-row">
                      {[
                        {
                          key: "name",
                          label: "Full Name *",
                          placeholder: "Your name",
                          type: "text",
                        },
                        {
                          key: "email",
                          label: "Email Address *",
                          placeholder: "you@example.com",
                          type: "email",
                        },
                      ].map(({ key, label, placeholder, type }) => (
                        <div key={key}>
                          <label className="form-label">{label}</label>
                          <input
                            type={type}
                            value={form[key]}
                            onChange={set(key)}
                            onFocus={() => setFocused(key)}
                            onBlur={() => setFocused(null)}
                            placeholder={placeholder}
                            style={inputStyle(key)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Subject</label>
                      <select
                        value={form.subject}
                        onChange={set("subject")}
                        onFocus={() => setFocused("subject")}
                        onBlur={() => setFocused(null)}
                        style={{
                          ...inputStyle("subject"),
                          appearance: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">Select a topic…</option>
                        {subjects.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-field">
                      <label className="form-label">Country / Region</label>
                      <input
                        value={form.country}
                        onChange={set("country")}
                        onFocus={() => setFocused("country")}
                        onBlur={() => setFocused(null)}
                        placeholder="Where are you reaching out from?"
                        style={inputStyle("country")}
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-label">Your Message *</label>
                      <textarea
                        value={form.message}
                        onChange={set("message")}
                        onFocus={() => setFocused("message")}
                        onBlur={() => setFocused(null)}
                        placeholder="Tell us how we can help or collaborate…"
                        rows={5}
                        style={{
                          ...inputStyle("message"),
                          resize: "vertical",
                          lineHeight: 1.7,
                        }}
                      />
                    </div>

                    <button
                      className="submit-btn"
                      onClick={() => canSubmit && setSent(true)}
                      disabled={!canSubmit}
                    >
                      Send Message
                    </button>
                    <p className="form-privacy">
                      We respect your privacy. Your details are never shared.
                    </p>
                  </>
                )}
              </div>
            </Reveal>

            {/* ── Sidebar ── */}
            <div>
              <Reveal delay={0.12}>
                <div className="sidebar-card">
                  <h3>Response Times</h3>
                  {[
                    {
                      icon: "💬",
                      label: "General Enquiries",
                      val: "Within 24–48 hours",
                    },
                    {
                      icon: "🤝",
                      label: "Partnership Requests",
                      val: "Within 3–5 business days",
                    },
                    {
                      icon: "📰",
                      label: "Media & Press",
                      val: "Within 24 hours",
                    },
                    {
                      icon: "🛠️",
                      label: "Technical Support",
                      val: "Within 12–24 hours",
                    },
                  ].map((item) => (
                    <div className="info-item" key={item.label}>
                      <span className="info-icon">{item.icon}</span>
                      <div>
                        <div className="info-label">{item.label}</div>
                        <div className="info-val">{item.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="sidebar-card">
                  <h3>Join the Conversation</h3>
                  {socials.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon-wrap">{s.svg}</div>
                      <div className="social-info">
                        <div className="social-name">{s.label}</div>
                        <div className="social-handle">{s.handle}</div>
                      </div>
                      <span className="social-arrow">→</span>
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* ════ FAQ ════ */}
        <div className="faq-section">
          <div className="faq-inner">
            <Reveal>
              <div className="faq-eyebrow">Got Questions?</div>
              <h2 className="faq-heading">Frequently Asked Questions</h2>
            </Reveal>
            {faqs.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className={`faq-item${openFaq === i ? " open" : ""}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <div className="faq-question">
                    <span className="faq-q-text">{item.q}</span>
                    <span className="faq-toggle">+</span>
                  </div>
                  <div className="faq-answer">
                    <div className="faq-answer-inner">{item.a}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* ── Tricolor stripe ── */}
        <div className="tricolor">
          <div className="tricolor-band" style={{ background: "#FF9933" }} />
          <div
            className="tricolor-band"
            style={{ background: "#fff", opacity: 0.4 }}
          />
          <div className="tricolor-band" style={{ background: "#138808" }} />
        </div>
      </div>
    </>
  );
}
